// Pipeline de um edital aprovado: etapas, conversa, anexos e histórico. Vale para o app e o backoffice.
import { z } from 'zod';

export const ETAPAS = [
  'recebido',
  'interessado',
  'preparando',
  'em_disputa',
  'aguardando_resultado',
  'venceu',
  'perdeu',
  'desistiu',
  'suspenso',
] as const;
export type Etapa = (typeof ETAPAS)[number];
export const ETAPA_NOMES: Record<Etapa, string> = {
  recebido: 'Recebido',
  interessado: 'Vou participar',
  preparando: 'Preparando proposta',
  em_disputa: 'Em disputa',
  aguardando_resultado: 'Aguardando resultado',
  venceu: 'Venceu',
  perdeu: 'Perdeu',
  desistiu: 'Desisti',
  suspenso: 'Suspenso / revogado',
};
/** Etapas finais pedem motivo (resultado). */
export const ETAPAS_FINAIS: readonly Etapa[] = ['venceu', 'perdeu', 'desistiu', 'suspenso'];

export const EtapaInput = z.object({
  etapa: z.enum(ETAPAS),
  /** Obrigatório nas etapas finais: valor vencedor, colocação, motivo da desistência. */
  motivo: z.string().trim().max(1000).nullable().default(null),
});

export const ComentarioInput = z.object({ texto: z.string().trim().min(1).max(4000) });

export const Evento = z.object({
  id: z.string().uuid(),
  tipo: z.enum(['etapa', 'comentario', 'anexo', 'anexo_removido', 'interesse', 'triagem']),
  lado: z.enum(['cliente', 'equipe', 'sistema']),
  autor: z.string().nullable(),
  de: z.string().nullable(),
  para: z.string().nullable(),
  texto: z.string().nullable(),
  anexoId: z.string().uuid().nullable(),
  em: z.string(),
});
export type Evento = z.infer<typeof Evento>;
export const EventosResponse = z.object({ itens: z.array(Evento) });

export const Anexo = z.object({
  id: z.string().uuid(),
  nome: z.string(),
  contentType: z.string(),
  tamanhoBytes: z.number(),
  lado: z.enum(['cliente', 'equipe']),
  autor: z.string().nullable(),
  em: z.string(),
});
export type Anexo = z.infer<typeof Anexo>;
export const AnexosResponse = z.object({ itens: z.array(Anexo) });

/** Upload: corpo `application/octet-stream`, nome no header `x-nome` (URL-encoded). Até 25 MB. */
export const ANEXO_MAX_BYTES = 25 * 1024 * 1024;
export const ANEXO_TIPOS: Record<string, string> = {
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.zip': 'application/zip',
  '.txt': 'text/plain',
  '.csv': 'text/csv',
};

/** Estado do pipeline embutido nos itens de fit (app e backoffice). */
export const Pipeline = z.object({
  etapa: z.enum(ETAPAS),
  etapaEm: z.string().nullable(),
  resultadoMotivo: z.string().nullable(),
  comentarios: z.number(),
  anexos: z.number(),
});
export type Pipeline = z.infer<typeof Pipeline>;
